import { create } from "zustand"
import { persist } from "zustand/middleware"

export type TaskType = "medication-selection" | "consultation-type" | "supplementary-questionnaire"
export type MedicationType = "Compounded_Semaglutide" | "Compounded_Tirzepatide" | "Zepbound"

interface DashboardBannerState {
  // Current task state
  currentTask: TaskType | null
  selectedMedication: MedicationType | null
  isSupplementaryModalOpen: boolean

  // Task completion tracking
  isMedicationSelected: boolean
  isConsultationTypeSelected: boolean
  isSupplementaryCompleted: boolean

  // Actions
  setCurrentTask: (task: TaskType | null) => void
  setSelectedMedication: (medication: MedicationType | null) => void
  setSupplementaryModalOpen: (open: boolean) => void
  setMedicationSelected: (completed: boolean) => void
  setConsultationTypeSelected: (completed: boolean) => void
  setSupplementaryCompleted: (completed: boolean) => void
  resetBannerState: () => void
}

export const useDashboardBannerStore = create<DashboardBannerState>()(
  persist(
    (set) => ({
      // Initial state
      currentTask: "medication-selection", // Start with medication selection
      selectedMedication: null,
      isSupplementaryModalOpen: false,
      isMedicationSelected: false,
      isConsultationTypeSelected: false,
      isSupplementaryCompleted: false,

      // Actions
      setCurrentTask: (task) => set({ currentTask: task }),
      setSelectedMedication: (medication) => set({ selectedMedication: medication }),
      setSupplementaryModalOpen: (open) => set({ isSupplementaryModalOpen: open }),
      setMedicationSelected: (completed) => {
        set({ isMedicationSelected: completed })
        if (completed) {
          set({ currentTask: "consultation-type" })
        }
      },
      setConsultationTypeSelected: (completed) => {
        set({ isConsultationTypeSelected: completed })
        if (completed) {
          set({ currentTask: "supplementary-questionnaire" })
        }
      },
      setSupplementaryCompleted: (completed) => {
        set({ isSupplementaryCompleted: completed })
        if (completed) {
          set({ currentTask: null }) // No more tasks
        }
      },
      resetBannerState: () =>
        set({
          currentTask: "medication-selection",
          selectedMedication: null,
          isSupplementaryModalOpen: false,
          isMedicationSelected: false,
          isConsultationTypeSelected: false,
          isSupplementaryCompleted: false,
        }),
    }),
    {
      name: "dashboard-banner-storage",
      onRehydrateStorage: (state) => {
        if (state) {
          if (!state.isMedicationSelected) {
            state.currentTask = "medication-selection";
          } else if (!state.isConsultationTypeSelected) {
            state.currentTask = "consultation-type";
          } else if (!state.isSupplementaryCompleted) {
            state.currentTask = "supplementary-questionnaire";
          } else {
            state.currentTask = null;
          }
        }
      },
    },
  ),
)
